import { useState } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
    AcademicCapIcon,
    UserGroupIcon,
    TrophyIcon,
    HeartIcon,
    CheckCircleIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline'
import EnquiryModal from '../../components/Enquiry/EnquiryModal'

const About = () => {
    const navigate = useNavigate()
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)
    const [enquiryType, setEnquiryType] = useState('consultation')

    const stats = [
        { number: '10,000+', label: 'Students Helped', icon: UserGroupIcon },
        { number: '50+', label: 'Partner Universities', icon: AcademicCapIcon },
        { number: '98%', label: 'Success Rate', icon: TrophyIcon },
        { number: '24/7', label: 'Support Available', icon: HeartIcon },
    ]

    const values = [
        {
            title: 'Excellence',
            description: 'We strive for excellence in everything we do, ensuring the highest quality education guidance.',
            icon: TrophyIcon,
        },
        {
            title: 'Integrity',
            description: 'We maintain the highest standards of integrity and transparency in all our interactions.',
            icon: CheckCircleIcon,
        },
        {
            title: 'Innovation',
            description: 'We embrace innovation to provide cutting-edge solutions for education guidance.',
            icon: AcademicCapIcon,
        },
        {
            title: 'Care',
            description: 'We care deeply about our students\' success and are committed to their growth.',
            icon: HeartIcon,
        },
    ]



    return (
        <>
            <Helmet>
                <title>About Us | Margdarshak</title>
                <meta name="description" content="Learn about Margdarshak's mission to provide quality education guidance. Meet our expert team and discover our commitment to student success." />
                <meta name="keywords" content="about margdarshak, education guidance, career counseling, online education, student success" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-20">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-primary-600 via-secondary-600 to-primary-800 text-white py-16 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                                About <span className="text-accent-400">Margdarshak</span>
                            </h1>
                            <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Your trusted partner in education guidance, helping students achieve their career dreams
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Mission Section */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                                    Our Mission
                                </h2>
                                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                    At Margdarshak, we believe that quality education should be accessible to everyone.
                                    Our mission is to guide students toward the right educational path that aligns with
                                    their career aspirations and personal goals.
                                </p>
                                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                    We partner with top universities and institutions to provide comprehensive
                                    education guidance, from course selection to career planning, ensuring our
                                    students make informed decisions for their future.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        className="btn-primary btn-lg"
                                        onClick={() => {
                                            setEnquiryType('consultation')
                                            setIsEnquiryOpen(true)
                                        }}
                                    >
                                        Get Started
                                    </button>
                                    <button
                                        className="btn-outline btn-lg"
                                        onClick={() => navigate('/courses')}
                                    >
                                        Learn More
                                    </button>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="relative"
                            >
                                <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8">
                                    <div className="grid grid-cols-2 gap-6">
                                        {stats.map((stat, index) => {
                                            const Icon = stat.icon
                                            return (
                                                <div key={index} className="text-center">
                                                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <Icon className="w-8 h-8 text-primary-600" />
                                                    </div>
                                                    <div className="text-2xl font-bold text-gray-900 mb-1">
                                                        {stat.number}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Values Section */}
                <section className="py-16 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-12"
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                                Our Values
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                The principles that guide everything we do
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {values.map((value, index) => {
                                const Icon = value.icon
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="card p-6 text-center hover-lift"
                                    >
                                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Icon className="w-8 h-8 text-primary-600" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                            {value.title}
                                        </h3>
                                        <p className="text-gray-600">
                                            {value.description}
                                        </p>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
                                Ready to Start Your Journey?
                            </h2>
                            <p className="text-xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Join thousands of students who have transformed their careers with our guidance
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    className="btn-lg bg-white text-primary-600 hover:bg-gray-50"
                                    onClick={() => {
                                        setEnquiryType('consultation')
                                        setIsEnquiryOpen(true)
                                    }}
                                >
                                    Get Free Consultation
                                </button>
                                <button
                                    className="btn-lg border-2 border-white text-white hover:bg-white hover:text-primary-600"
                                    onClick={() => navigate('/courses')}
                                >
                                    Explore Courses
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Enquiry Modal */}
                <EnquiryModal
                    isOpen={isEnquiryOpen}
                    onClose={() => setIsEnquiryOpen(false)}
                    enquiryType={enquiryType}
                />
            </div>
        </>
    )
}

export default About
